// streamingUtils.ts - NDJSON streaming response utilities for chunked uploaders

interface StreamChunk<T = Record<string, unknown>> {
  type?: string;
  data?: T;
  error?: string;
  status?: string;
  progress?: number;
  total_rows?: number;
  [key: string]: unknown;
}

export interface StreamProcessingOptions<T> {
  onChunk?: (chunk: T) => void;
  onProgress?: (progress: number, total: number) => void;
  onError?: (error: string) => void;
  skipStatusMessages?: boolean;
}

/**
 * Process a streaming NDJSON response
 * Backend sends newline-delimited JSON objects
 * @param response - Fetch Response with streaming body
 * @param options - Processing options
 */
export async function processNDJSONStream<T = Record<string, unknown>>(
  response: Response,
  options: StreamProcessingOptions<T> = {}
): Promise<T[]> {
  const reader = response.body?.getReader();

  if (!reader) {
    throw new Error('Response not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  const accumulatedData: T[] = [];

  const { onChunk, onProgress, onError, skipStatusMessages = true } = options;

  try {
    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        // Process any remaining data in buffer
        if (buffer.trim()) {
          processLine(buffer.trim());
        }
        break;
      }

      const decodedChunk = decoder.decode(value, { stream: true });
      buffer += decodedChunk;
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line) continue;
        processLine(line.trim());
      }
    }
  } finally {
    reader.releaseLock();
  }

  function processLine(line: string): void {
    if (!line) return;

    // Skip response status messages
    if (skipStatusMessages && line.includes('<Response streamed')) {
      return;
    }

    // Try to parse as JSON
    if (line.startsWith('{') && line.endsWith('}')) {
      try {
        const chunk = JSON.parse(line) as StreamChunk<T>;

        // Handle error chunks
        if (chunk.error) {
          if (onError) {
            onError(chunk.error);
          }
          throw new Error(chunk.error);
        }

        // Handle progress updates
        if (chunk.total_rows !== undefined && onProgress) {
          onProgress(chunk.progress || 0, chunk.total_rows);
        }

        // Handle complete status
        if (chunk.status === 'complete') {
          return; // Don't add to accumulated data
        }

        // Handle data chunks
        if (chunk.data !== undefined) {
          accumulatedData.push(chunk.data);
          if (onChunk) {
            onChunk(chunk.data);
          }
        } else if (chunk.type !== 'meta' && chunk.type !== 'progress') {
          // For chunks without explicit data field (like {UTC: ..., Value: ...})
          // Check if it looks like data (has UTC field or similar)
          if ('UTC' in chunk || Object.keys(chunk).length > 2) {
            accumulatedData.push(chunk as unknown as T);
            if (onChunk) {
              onChunk(chunk as unknown as T);
            }
          }
        }
      } catch {
        // Continue - some lines might not be valid JSON
      }
    }
  }

  return accumulatedData;
}

/**
 * Wait for a Socket.IO completion event
 * Used to ensure frontend doesn't leave room before backend finishes
 * @param checkFn - Function that returns true when complete
 * @param timeoutMs - Maximum time to wait
 */
export function waitForCompletion(
  checkFn: () => boolean,
  timeoutMs: number = 120000
): Promise<void> {
  return new Promise((resolve) => {
    // If already complete, resolve immediately
    if (checkFn()) {
      resolve();
      return;
    }

    // Poll for completion
    const checkInterval = setInterval(() => {
      if (checkFn()) {
        clearInterval(checkInterval);
        clearTimeout(timeout);
        resolve();
      }
    }, 100);

    // Timeout fallback
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      resolve();
    }, timeoutMs);
  });
}


